import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Footer from '../../Footer/Footer';
import { getCanonicalUrl } from '../../../utils/seo';

const HERO_IMG = 'https://BelvoirCastle.b-cdn.net/Newsletter%20Images/Belvoir%20retail%20village-35.webp';
const ROOM_IMG = 'https://BelvoirCastle.b-cdn.net/Newsletter%20Images/Picture%202.webp';
const COFFEE_IMG = 'https://BelvoirCastle.b-cdn.net/BelvoirBistro/EX2A6967.jpg';
const PAGE_PATH = 'whats-on/members-coffee-cake-morning';
const BOOK_URL = '/booking-journey?locationId=978&pageId=44224497-08b1-4e3b-937c-6e0179c92d84';

const C = {
  navy: '#101d40',
  gold: '#C9963C',
  goldDk: '#A67C2A',
  ivory: '#FAF7F0',
  parchment: '#F3EDD8',
  ink: '#1c1c1e',
  muted: 'rgba(28, 28, 30, 0.68)',
  line: 'rgba(16, 29, 64, 0.12)',
};

const Page = styled.div`
  min-height: 100vh;
  background: ${C.ivory};
  padding-top: 158px;
  color: ${C.ink};

  @media (max-width: 768px) {
    padding-top: clamp(188px, 34vw, 228px);
  }
`;

const Hero = styled.section`
  position: relative;
  height: clamp(520px, 72vh, 720px);
  display: flex;
  align-items: flex-end;
  overflow: hidden;
  background: ${C.navy};

  @media (max-width: 768px) {
    height: clamp(540px, 86svh, 700px);
  }
`;

const HeroPhoto = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 42%;
`;

const HeroShade = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(16, 29, 64, 0.94) 0%,
    rgba(16, 29, 64, 0.62) 34%,
    rgba(16, 29, 64, 0.16) 62%,
    rgba(16, 29, 64, 0.05) 100%
  );
`;

const HeroCopy = styled.div`
  position: relative;
  z-index: 1;
  width: min(1120px, calc(100% - clamp(2rem, 6vw, 4rem)));
  margin: 0 auto;
  padding: 0 0 clamp(2rem, 5vw, 3.25rem);
  color: #fff;

  h1,
  h1 em {
    text-shadow: 0 2px 4px rgba(10, 20, 40, 0.95), 0 10px 28px rgba(10, 20, 40, 0.75);
  }
`;

const Kicker = styled.p`
  margin: 0 0 0.7rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
  text-shadow: ${p => (p.$hero ? '0 1px 2px rgba(10, 20, 40, 0.9), 0 4px 18px rgba(10, 20, 40, 0.75)' : 'none')};
`;

const Title = styled.h1`
  margin: 0 0 0.7rem;
  max-width: 14ch;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2.7rem, 6vw, 4.6rem);
  font-weight: 600;
  line-height: 0.95;
  color: #fff;
  text-shadow: ${p => (p.$hero ? '0 2px 4px rgba(10, 20, 40, 0.85), 0 8px 28px rgba(10, 20, 40, 0.65)' : 'none')};

  em {
    font-style: italic;
    font-weight: 500;
    color: ${C.gold};
  }
`;

const Lead = styled.p`
  margin: 0 0 1.4rem;
  max-width: 36rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.15rem, 2vw, 1.45rem);
  font-style: italic;
  font-weight: 500;
  line-height: 1.35;
  color: rgba(255, 255, 255, 0.88);
`;

const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.38rem 0.85rem;
  border-radius: 999px;
  border: 1px solid ${p => (p.$fill ? 'transparent' : 'rgba(255, 255, 255, 0.28)')};
  background: ${p => (p.$fill ? C.gold : 'rgba(255, 255, 255, 0.1)')};
  color: #fff;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const BookLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 3rem;
  padding: 0.85rem 1.6rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  text-decoration: none;

  &:hover {
    background: ${C.goldDk};
    color: #fff;
  }
`;

const GhostLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 3rem;
  padding: 0.85rem 1.6rem;
  border-radius: 999px;
  border: 1px solid ${p => (p.$onDark ? 'rgba(255, 255, 255, 0.45)' : C.navy)};
  background: transparent;
  color: ${p => (p.$onDark ? '#fff' : C.navy)};
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  text-decoration: none;

  &:hover {
    background: ${p => (p.$onDark ? '#fff' : C.navy)};
    color: ${p => (p.$onDark ? C.navy : '#fff')};
  }
`;

const Facts = styled.section`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  width: min(1120px, calc(100% - clamp(2rem, 6vw, 4rem)));
  margin: 0 auto;
  border-bottom: 1px solid ${C.line};

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Fact = styled.article`
  padding: 1.35rem 0;

  & + & {
    padding-left: 1.5rem;
    border-left: 1px solid ${C.line};
  }

  @media (max-width: 720px) {
    padding: 1rem 0;

    & + & {
      padding-left: 0;
      border-left: 0;
      border-top: 1px solid ${C.line};
    }
  }
`;

const FactLabel = styled.p`
  margin: 0 0 0.3rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${C.goldDk};
`;

const FactValue = styled.p`
  margin: 0;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.45rem, 2vw, 1.85rem);
  font-weight: 600;
  line-height: 1.15;
  color: ${C.navy};
`;

const Story = styled.section`
  width: min(1120px, calc(100% - clamp(2rem, 6vw, 4rem)));
  margin: 0 auto;
  padding: clamp(2rem, 4vw, 3rem) 0;
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  gap: clamp(1.5rem, 3vw, 2.5rem);
  align-items: center;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const Copy = styled.div``;

const Heading = styled.h2`
  margin: 0 0 0.85rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2.1rem, 4vw, 3rem);
  font-weight: 600;
  line-height: 1.05;
  color: ${C.navy};
`;

const Body = styled.p`
  margin: 0 0 1rem;
  max-width: 38rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.2rem;
  line-height: 1.55;
  color: ${C.ink};
`;

const Note = styled.p`
  margin: 1.25rem 0 0;
  padding-top: 1rem;
  border-top: 1px solid ${C.line};
  max-width: 38rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.92rem;
  line-height: 1.6;
  color: ${C.muted};
`;

const Photo = styled.figure`
  margin: 0;
  border-radius: 14px;
  overflow: hidden;

  img {
    width: 100%;
    aspect-ratio: 3 / 2;
    object-fit: cover;
    object-position: center;
    display: block;
  }
`;

const Aside = styled.section`
  background: ${C.parchment};
`;

const AsideInner = styled.div`
  width: min(1120px, calc(100% - clamp(2rem, 6vw, 4rem)));
  margin: 0 auto;
  padding: clamp(2rem, 4vw, 3rem) 0;
  display: grid;
  grid-template-columns: 0.85fr 1.15fr;
  gap: clamp(1.5rem, 3vw, 2.5rem);
  align-items: center;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const AsidePhoto = styled.img`
  width: 100%;
  aspect-ratio: 3 / 2;
  object-fit: cover;
  object-position: center;
  display: block;
  border-radius: 14px;
`;

const Meet = styled.section`
  width: min(1120px, calc(100% - clamp(2rem, 6vw, 4rem)));
  margin: 0 auto;
  padding: clamp(2rem, 4vw, 3rem) 0 clamp(2.25rem, 5vw, 3.5rem);
  border-top: 1px solid ${C.line};
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  gap: clamp(1.5rem, 3vw, 2.5rem);
  align-items: center;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const JoinCard = styled.div`
  background: ${C.navy};
  color: #f8f7f4;
  border-radius: 14px;
  padding: clamp(1.5rem, 3vw, 2rem);
`;

const BookClose = styled.section`
  background: ${C.navy};
  color: #f8f7f4;
  text-align: center;
`;

const BookCloseInner = styled.div`
  width: min(720px, calc(100% - clamp(2rem, 6vw, 4rem)));
  margin: 0 auto;
  padding: clamp(2.5rem, 5vw, 3.5rem) 0;
`;

const BookCloseText = styled.p`
  margin: 0 0 0.85rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.6rem, 3vw, 2.15rem);
  font-weight: 600;
  line-height: 1.2;
  color: #fff;
`;

const BookCloseLead = styled.p`
  margin: 0 auto 1.5rem;
  max-width: 38rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 1.05rem;
  font-weight: 400;
  line-height: 1.6;
  color: #fff;
`;

const CoffeeCakeMorning = () => {
  const canonical = getCanonicalUrl(PAGE_PATH);

  return (
    <>
      <Helmet>
        <title>Members' Coffee &amp; Cake Morning | Belvoir Castle</title>
        <meta
          name="description"
          content="Members' Coffee & Cake Morning on Thursday 12 November 2026, from 9:30-12:00, on the top floor of Belvoir Bistro. Coffee and cake £8.00. Limited spaces available. Booking recommended."
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="Members' Coffee & Cake Morning" />
        <meta property="og:description" content="Thursday 12 November, from 9:30-12:00. Coffee and cake for £8.00 on the top floor of Belvoir Bistro." />
        <meta property="og:image" content={HERO_IMG} />
        <meta property="og:url" content={canonical} />
      </Helmet>
      <Page>
        <Hero>
          <HeroPhoto src={HERO_IMG} alt="Belvoir Bistro and its terrace at Belvoir Retail Village" />
          <HeroShade />
          <HeroCopy>
            <Title $hero>
              Members' <em>Coffee &amp; Cake</em> Morning
            </Title>
            <Lead>
              Thursday 12 November, on the top floor of Belvoir Bistro.
            </Lead>
            <Pills>
              <Pill $fill>Members exclusive</Pill>
              <Pill>from 9:30-12:00</Pill>
              <Pill>Coffee and cake £8.00</Pill>
            </Pills>
            <ButtonRow>
              <BookLink to={BOOK_URL}>Book your place</BookLink>
              <GhostLink to="/retail-village/dining/belvoir-bistro" $onDark>
                View the Bistro
              </GhostLink>
            </ButtonRow>
          </HeroCopy>
        </Hero>

        <Facts>
          <Fact>
            <FactLabel>When</FactLabel>
            <FactValue>Thursday 12 November</FactValue>
          </Fact>
          <Fact>
            <FactLabel>Time</FactLabel>
            <FactValue>from 9:30-12:00</FactValue>
          </Fact>
          <Fact>
            <FactLabel>Where</FactLabel>
            <FactValue>Top floor, Belvoir Bistro</FactValue>
          </Fact>
        </Facts>

        <Story>
          <Copy>
            <Kicker>A thank you to our members</Kicker>
            <Heading>Coffee, cake and good company</Heading>
            <Body>
              Please bring your membership card with you and show it when you order. Enjoy a coffee and a slice of cake for £8.00, and drop in any time from 9:30-12:00.
            </Body>
            <Body>
              Spaces are limited, so booking is recommended. Reserving a place online is free. You pay the £8.00 when you order at the Bistro.
            </Body>
            <Note>
              The £8.00 deal is already discounted, so the members' discount cannot be used on top of it. Your usual 10% members' discount still applies to anything else you order.
            </Note>
          </Copy>
          <Photo>
            <img src={ROOM_IMG} alt="Tables and chairs inside Belvoir Bistro" />
          </Photo>
        </Story>

        <Aside>
          <AsideInner>
            <AsidePhoto src={COFFEE_IMG} alt="A freshly made coffee at Belvoir Bistro" />
            <Copy>
              <Heading>Stay a While at the Bistro</Heading>
              <Kicker>Belvoir Retail Village</Kicker>
              <Body>
                After your coffee and cake, why not stay for lunch or browse the shops at Belvoir Retail Village? It's the perfect way to make a morning of it.
              </Body>
              <GhostLink to="/retail-village/dining/belvoir-bistro">View the Bistro</GhostLink>
            </Copy>
          </AsideInner>
        </Aside>

        <Meet>
          <div>
            <Kicker>Have your say</Kicker>
            <Heading>Meet the team</Heading>
            <Body>
              A representative from the Castle will be joining us on the morning, making this a great opportunity to share your feedback on your membership. Tell us what you enjoy, what we could do better, and what you would like to see at Belvoir in the future.
            </Body>
            <Body>
              Whether it's new events, member benefits or ideas for the year ahead, we'd love to hear from you.
            </Body>
          </div>
          <JoinCard>
            <Kicker>Not a member?</Kicker>
            <Heading style={{ color: '#fff', marginBottom: '1.25rem' }}>Become one today</Heading>
            <BookLink to="/memberships">Become a member</BookLink>
          </JoinCard>
        </Meet>
        <BookClose>
          <BookCloseInner>
            <Kicker>Thursday 12 November</Kicker>
            <BookCloseText>Limited spaces available. Booking recommended.</BookCloseText>
            <BookCloseLead>
              Reserve your complimentary place from 9:30am to 12:00pm in the top floor restaurant at Belvoir Bistro. Please bring your membership card. Coffee and cake are available for £8.00 per person, payable when ordering.
            </BookCloseLead>
            <BookLink to={BOOK_URL}>Book your place</BookLink>
          </BookCloseInner>
        </BookClose>
        <Footer />
      </Page>
    </>
  );
};

export default CoffeeCakeMorning;
